import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaStar,
  FaSpinner,
  FaMapMarkerAlt,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaWrench,
  FaImage,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { publicGarageService } from "../services/apiService";
import { useAuth } from "../auth/AuthContext";

const TIER_STYLES = {
  Small: "bg-slate-100 text-slate-700 border-slate-200",
  Medium: "bg-blue-50 text-blue-800 border-blue-200",
  Enterprise: "bg-amber-50 text-amber-900 border-amber-200",
};

function PhotoPlaceholder() {
  return (
    <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 sm:aspect-[16/9]">
      <div className="px-4 text-center text-gray-400">
        <FaImage className="mx-auto text-2xl opacity-60 sm:text-3xl" />
        <p className="mt-2 text-xs font-medium sm:text-sm">No photos yet</p>
      </div>
    </div>
  );
}

function PhotoGallery({ photos }) {
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState({});
  const list = Array.isArray(photos) ? photos.filter(Boolean) : [];
  const visible = list.filter((_, idx) => !broken[idx]);

  useEffect(() => {
    setActive(0);
    setBroken({});
  }, [photos]);

  if (!visible.length) {
    return <PhotoPlaceholder />;
  }

  const safeActive = Math.min(active, visible.length - 1);
  const currentSrc = visible[safeActive];

  return (
    <div className="w-full">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-gray-100 sm:aspect-[16/9] lg:aspect-[4/3]">
        <img
          src={currentSrc}
          alt={`Workshop ${safeActive + 1}`}
          className="h-full w-full object-cover"
          onError={() => {
            const originalIdx = list.indexOf(currentSrc);
            setBroken((prev) => ({ ...prev, [originalIdx]: true }));
          }}
        />
        {visible.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setActive((i) => (i - 1 + visible.length) % visible.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 sm:left-3"
              aria-label="Previous photo"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              onClick={() => setActive((i) => (i + 1) % visible.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 sm:right-3"
              aria-label="Next photo"
            >
              <FaChevronRight />
            </button>
          </>
        )}
      </div>
      {visible.length > 1 && (
        <div className="mt-2 flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 scrollbar-hide">
          {visible.map((src, idx) => (
            <button
              key={`${src}-${idx}`}
              type="button"
              onClick={() => setActive(idx)}
              className={`snap-start h-12 w-16 shrink-0 overflow-hidden rounded-lg border-2 sm:h-14 sm:w-20 ${
                idx === safeActive ? "border-blue-500" : "border-transparent opacity-70"
              }`}
            >
              <img src={src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewStars({ rating }) {
  const full = Math.round(Number(rating) || 0);
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-500" aria-label={`${rating} stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <FaStar key={i} className={`text-xs sm:text-sm ${i < full ? "" : "text-gray-200"}`} />
      ))}
    </span>
  );
}

function BookCta({
  fullyBooked,
  bookingMode = 'instant',
  onBook,
  onWaitlist,
  onQuote,
  onProjectIntake,
  className = "",
}) {
  if (bookingMode === 'quote') {
    return (
      <button
        type="button"
        onClick={onQuote}
        className={`w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 sm:text-base ${className}`}
      >
        Request Custom Quote
      </button>
    );
  }

  if (bookingMode === 'staged_quote') {
    return (
      <button
        type="button"
        onClick={onProjectIntake}
        className={`w-full rounded-xl bg-purple-600 py-3 text-sm font-semibold text-white hover:bg-purple-700 sm:text-base ${className}`}
      >
        Start Project Intake
      </button>
    );
  }

  if (fullyBooked) {
    return (
      <div className={`text-center ${className}`}>
        <p className="text-sm font-semibold text-amber-800">Fully booked — add to waitlist.</p>
        <p className="mt-1 text-xs text-gray-500">No slots available in the next 7 days.</p>
        <button
          type="button"
          onClick={onWaitlist}
          className="mt-3 w-full rounded-xl border border-amber-300 bg-amber-50 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-100"
        >
          Add to waitlist
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onBook}
      className={`w-full rounded-xl bg-gray-900 py-3 text-sm font-semibold text-white hover:bg-gray-800 sm:text-base ${className}`}
    >
      Book Service
    </button>
  );
}

export default function GarageProfile() {
  const { garageId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState({ items: [], pagination: { page: 1, pages: 1, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState("");

  const loadProfile = useCallback(
    async (reviewPage = 1) => {
      if (reviewPage === 1) setLoading(true);
      else setReviewLoading(true);
      setError("");
      try {
        const data = await publicGarageService.getProfile(garageId, { reviewPage });
        if (reviewPage === 1) {
          setProfile(data.garage);
          setReviews(data.reviews || { items: [], pagination: {} });
        } else {
          setReviews((prev) => ({
            items: [...(prev.items || []), ...(data.reviews?.items || [])],
            pagination: data.reviews?.pagination || prev.pagination,
          }));
        }
      } catch (err) {
        setError(err.message || "Failed to load garage profile.");
        if (reviewPage === 1) setProfile(null);
      } finally {
        setLoading(false);
        setReviewLoading(false);
      }
    },
    [garageId]
  );

  useEffect(() => {
    loadProfile(1);
  }, [loadProfile]);

  const handleBookService = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/garages/${garageId}` } });
      return;
    }
    navigate(`/service/booking/${garageId}`);
  };

  const handleWaitlist = () => {
    toast.success("Added to waitlist — we'll notify you when a slot opens.");
  };

  const handleQuoteRequest = () => {
    navigate(`/garage/quote-request?garageId=${encodeURIComponent(garageId)}`);
  };

  const handleProjectIntake = () => {
    navigate(`/garage/project-intake?garageId=${encodeURIComponent(garageId)}`);
  };

  const loadMoreReviews = () => {
    const next = (reviews.pagination?.page || 1) + 1;
    if (next <= (reviews.pagination?.pages || 1)) {
      loadProfile(next);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] w-full items-center justify-center px-4">
        <FaSpinner className="animate-spin text-2xl text-blue-500" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-12 text-center">
        <p className="text-red-600">{error || "Garage not found."}</p>
        <Link to="/garages" className="mt-4 inline-block text-sm font-medium text-blue-600 hover:underline">
          ← Back to garage search
        </Link>
      </div>
    );
  }

  const tierClass = TIER_STYLES[profile.tier] || TIER_STYLES.Small;
  const hours =
    profile.openingTime && profile.closingTime
      ? `${profile.openingTime} – ${profile.closingTime}`
      : null;

  return (
    <div className="w-full px-4 pt-3 pb-28 sm:px-6 sm:pb-10 lg:px-8">
      <div className="mx-auto w-full max-w-6xl">
        <Link
          to="/garages"
          className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline sm:mb-4"
        >
          <FaChevronLeft className="text-xs" />
          Back to search
        </Link>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
          {/* Left — gallery + summary */}
          <div className="space-y-4 lg:col-span-5">
            <PhotoGallery photos={profile.photos} />

            <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase sm:text-xs ${tierClass}`}
                >
                  {profile.tier}
                </span>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900">
                  <FaStar className="text-amber-400" />
                  {Number(profile.rating).toFixed(1)}
                  {profile.reviewCount > 0 && (
                    <span className="font-normal text-gray-500">({profile.reviewCount})</span>
                  )}
                </span>
              </div>

              <h1 className="mt-2 text-lg font-bold text-gray-900 break-words sm:text-xl lg:text-2xl">
                {profile.displayName}
              </h1>

              {profile.location && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-gray-600 sm:text-sm">
                  <FaMapMarkerAlt className="mt-0.5 shrink-0 text-gray-400" />
                  <span className="break-words">{profile.location}</span>
                </p>
              )}

              {hours && (
                <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-600 sm:text-sm">
                  <FaClock className="shrink-0 text-gray-400" />
                  <span>{hours}</span>
                  {profile.stats?.isOpen === true && (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 sm:text-xs">
                      Open
                    </span>
                  )}
                  {profile.stats?.isOpen === false && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 sm:text-xs">
                      Closed
                    </span>
                  )}
                </p>
              )}

              {profile.description && (
                <p className="mt-3 text-xs leading-relaxed text-gray-600 sm:text-sm">{profile.description}</p>
              )}
            </div>

            {/* Desktop CTA */}
            <div className="hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:block">
              <BookCta
                fullyBooked={profile.fullyBooked}
                bookingMode={profile.bookingMode}
                onBook={handleBookService}
                onWaitlist={handleWaitlist}
                onQuote={handleQuoteRequest}
                onProjectIntake={handleProjectIntake}
              />
            </div>
          </div>

          {/* Right — services + reviews */}
          <div className="space-y-6 lg:col-span-7">
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">Services & prices</h2>
              <p className="mt-1 text-xs text-gray-500">
                {profile.priceBenchmarkNote || "Prices benchmarked by SparePe."}
              </p>
              <ul className="mt-3 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
                {(profile.services || []).length === 0 ? (
                  <li className="px-4 py-6 text-center text-sm text-gray-500">Services listed on request.</li>
                ) : (
                  profile.services.map((svc) => (
                    <li
                      key={svc.serviceId || svc.name}
                      className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-5"
                    >
                      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-900">
                        <FaWrench className="shrink-0 text-xs text-gray-400" />
                        <span className="break-words">{svc.name}</span>
                        {svc.packageEligible && (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                            Package
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-sm font-medium text-gray-700 sm:text-right">
                        {svc.priceLabel || "Price on visit"}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">Reviews</h2>
              {!reviews.items?.length ? (
                <p className="mt-3 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                  No reviews yet.
                </p>
              ) : (
                <ul className="mt-3 grid gap-3 sm:grid-cols-1">
                  {reviews.items.map((review) => (
                    <li key={review.id} className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <ReviewStars rating={review.rating} />
                        <span className="text-[11px] text-gray-400 sm:text-xs">
                          {review.createdAt
                            ? new Date(review.createdAt).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : ""}
                        </span>
                      </div>
                      <p className="mt-1 text-xs font-semibold text-gray-700">{review.reviewerName}</p>
                      {review.text && (
                        <p className="mt-2 text-sm text-gray-600 break-words">{review.text}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {reviews.pagination?.page < reviews.pagination?.pages && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMoreReviews}
                    disabled={reviewLoading}
                    className="inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60 sm:w-auto"
                  >
                    {reviewLoading ? <FaSpinner className="animate-spin" /> : null}
                    Older reviews
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA — above bottom nav */}
      <div className="fixed inset-x-0 bottom-16 z-20 border-t border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm lg:hidden">
        <div className="mx-auto w-full max-w-lg">
          <BookCta
            fullyBooked={profile.fullyBooked}
            bookingMode={profile.bookingMode}
            onBook={handleBookService}
            onWaitlist={handleWaitlist}
            onQuote={handleQuoteRequest}
            onProjectIntake={handleProjectIntake}
          />
        </div>
      </div>
    </div>
  );
}
